import { useEffect, useMemo, useRef, useState } from 'react'
import JsBarcode from 'jsbarcode'
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, cn } from '@saas-pages/shared/ui'
import { toolError, toolInvoke, toolSuccess } from '@saas-pages/shared/public/runtime'
import { useT, type Translations } from '@/shared/i18n'
import { ToolHeading } from '@/shared/ui/ToolHeading'

const ANALYTICS_KEY = 'barcode-generator'
const AUTO_DEBOUNCE_MS = 120

type BarcodeFormat = 'ean13' | 'ean8' | 'upc' | 'code128' | 'code39'
type FormatOption = {
  value: BarcodeFormat
  label: string
}

interface FormState {
  format: BarcodeFormat
  value: string
  lineColor: string
  background: string
  width: number
  height: number
  displayValue: boolean
}

interface GeneratedBarcode {
  format: BarcodeFormat
  value: string
  snapshot: FormState
  svg: string
  generatedAt: number
}

type PreviewState = 'empty' | 'valid' | 'stale' | 'pending' | 'invalid'

const initialForm: FormState = {
  format: 'ean13',
  value: '460123456789',
  lineColor: '#111827',
  background: '#ffffff',
  width: 2,
  height: 112,
  displayValue: true,
}

const formatOrder: BarcodeFormat[] = ['ean13', 'ean8', 'upc', 'code128', 'code39']

export function BarcodeGeneratorPage() {
  const t = useT()
  const svgRef = useRef<SVGSVGElement | null>(null)
  const debounceRef = useRef<number | undefined>(undefined)
  const previewGenerationRef = useRef(0)
  const mountedRef = useRef(false)
  const generatedRef = useRef<GeneratedBarcode | null>(null)
  const downloadInProgressRef = useRef(false)
  const [form, setForm] = useState<FormState>(initialForm)
  const [generated, setGenerated] = useState<GeneratedBarcode | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [previewState, setPreviewState] = useState<PreviewState>('empty')
  const [isDownloadingPng, setIsDownloadingPng] = useState(false)
  const [retryNonce, setRetryNonce] = useState(0)
  const formatOptions = buildFormatOptions(t)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      previewGenerationRef.current += 1
      if (debounceRef.current !== undefined) {
        window.clearTimeout(debounceRef.current)
        debounceRef.current = undefined
      }
    }
  }, [])

  useEffect(() => {
    const snapshot = { ...form }
    const errors = t.barcode.errors
    const token = previewGenerationRef.current + 1
    previewGenerationRef.current = token
    if (debounceRef.current !== undefined) window.clearTimeout(debounceRef.current)
    setPreviewState(generatedRef.current ? 'stale' : 'pending')
    setError(null)
    debounceRef.current = window.setTimeout(() => {
      debounceRef.current = undefined
      if (!mountedRef.current || token !== previewGenerationRef.current) return
      generatePreview(snapshot, errors, token)
    }, generatedRef.current ? AUTO_DEBOUNCE_MS : 0)
    return () => {
      if (debounceRef.current !== undefined) {
        window.clearTimeout(debounceRef.current)
        debounceRef.current = undefined
      }
    }
  }, [form, retryNonce, t.barcode.errors])

  const normalizedPreview = useMemo(() => {
    try {
      return normalizeBarcodeValue(form.format, form.value, t.barcode.errors)
    } catch {
      return form.value.trim()
    }
  }, [form.format, form.value, t.barcode.errors])

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setPreviewState(generatedRef.current ? 'stale' : 'pending')
    setError(null)
    setForm((current) => ({ ...current, [key]: value }))
  }

  function generatePreview(snapshot: FormState, errors: BarcodeErrors, token: number) {
    if (!mountedRef.current || token !== previewGenerationRef.current) return
    setPreviewState('pending')
    try {
      const svg = svgRef.current
      if (!svg) {
        throw new BarcodeInputError('preview_unavailable', errors.previewUnavailable)
      }

      const value = normalizeBarcodeValue(snapshot.format, snapshot.value, errors)
      clearSvg()
      JsBarcode(svg, value, {
        format: jsFormat(snapshot.format),
        width: snapshot.width,
        height: snapshot.height,
        displayValue: snapshot.displayValue,
        font: 'Inter, system-ui, sans-serif',
        fontSize: 18,
        textMargin: 8,
        margin: 18,
        lineColor: snapshot.lineColor,
        background: snapshot.background,
      })
      const serializedSvg = serializeSvg(svg)
      if (!mountedRef.current || token !== previewGenerationRef.current) return
      const nextGenerated = {
        format: snapshot.format,
        value,
        snapshot,
        svg: serializedSvg,
        generatedAt: Date.now(),
      }
      generatedRef.current = nextGenerated
      setGenerated(nextGenerated)
      setPreviewState('valid')
      setError(null)
    } catch (cause) {
      if (!mountedRef.current || token !== previewGenerationRef.current) return
      clearSvg()
      const barcodeError = normalizeError(cause)
      const localized = localizeBarcodeError(barcodeError, errors)
      generatedRef.current = null
      setGenerated(null)
      setPreviewState('invalid')
      setError(localized)
    }
  }

  function forceRegenerate() {
    previewGenerationRef.current += 1
    clearSvg()
    setPreviewState('pending')
    setError(null)
    setRetryNonce((current) => current + 1)
  }

  function downloadSvg() {
    if (!generated || previewState !== 'valid' || downloadInProgressRef.current) return
    downloadInProgressRef.current = true
    const startedAt = performance.now()
    const payload = { barcode_format: generated.format, export_format: 'svg' }
    toolInvoke(ANALYTICS_KEY, payload)
    let url: string | null = null
    try {
      setError(null)
      const blob = new Blob([generated.svg], { type: 'image/svg+xml;charset=utf-8' })
      url = URL.createObjectURL(blob)
      downloadUrl(url, filename(generated, 'svg'))
      toolSuccess(ANALYTICS_KEY, performance.now() - startedAt, payload)
    } catch {
      setError(t.barcode.errors.svgExportFailed)
      toolError(ANALYTICS_KEY, 'svg_export_failed', payload)
    } finally {
      downloadInProgressRef.current = false
      const urlToRevoke = url
      if (urlToRevoke) window.setTimeout(() => URL.revokeObjectURL(urlToRevoke), 1000)
    }
  }

  async function downloadPng() {
    if (!generated || previewState !== 'valid' || downloadInProgressRef.current) return

    downloadInProgressRef.current = true
    setIsDownloadingPng(true)
    setError(null)
    const startedAt = performance.now()
    const payload = { barcode_format: generated.format, export_format: 'png' }
    toolInvoke(ANALYTICS_KEY, payload)
    try {
      const pngUrl = await svgToPng(generated.svg)
      downloadUrl(pngUrl, filename(generated, 'png'))
      toolSuccess(ANALYTICS_KEY, performance.now() - startedAt, payload)
    } catch {
      setError(t.barcode.errors.pngExportFailed)
      toolError(ANALYTICS_KEY, 'png_export_failed', payload)
    } finally {
      downloadInProgressRef.current = false
      if (mountedRef.current) setIsDownloadingPng(false)
    }
  }

  function clearSvg() {
    const svg = svgRef.current
    if (!svg) return

    svg.replaceChildren()
    svg.removeAttribute('width')
    svg.removeAttribute('height')
    svg.removeAttribute('viewBox')
  }

  const canExport = previewState === 'valid' && generated !== null
  const statusText = previewState === 'valid'
    ? t.barcode.preview.ready
    : previewState === 'invalid'
      ? t.barcode.preview.invalid
      : previewState === 'stale'
        ? t.barcode.preview.pendingUpdate
        : previewState === 'pending'
          ? t.barcode.preview.generating
          : t.barcode.preview.empty

  return (
    <div className="mx-auto max-w-6xl">
      <ToolHeading title={t.barcode.hero.title} description={t.barcode.hero.description} />

      <div className="mt-4 grid gap-4 sm:mt-6 lg:grid-cols-[minmax(0,1fr)_420px] lg:gap-6">
        <Card className="min-w-0 rounded-lg lg:col-start-1">
          <CardContent className="space-y-4 p-4 sm:p-5">
            <div className="flex flex-wrap gap-2">
              {formatOptions.map((option) => (
                <button
                  aria-pressed={form.format === option.value}
                  key={option.value}
                  className={cn(
                    'min-h-9 rounded-md border px-3 py-1.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    form.format === option.value
                      ? 'border-foreground/30 bg-foreground text-background shadow-sm'
                      : 'bg-background hover:bg-accent/60',
                  )}
                  onClick={() => update('format', option.value)}
                  type="button"
                >
                  {option.label}
                </button>
              ))}
            </div>

            <label className="block space-y-2">
              <span className="text-sm font-medium">{t.barcode.fields.value}</span>
              <input
                className="h-11 w-full rounded-md border bg-background px-3 font-mono"
                onChange={(event) => update('value', event.target.value)}
                placeholder={t.barcode.fields.valuePlaceholder[form.format]}
                value={form.value}
              />
            </label>

            <p className="text-xs leading-5 text-muted-foreground">
              {t.barcode.formatHelp[form.format]}
            </p>
          </CardContent>
        </Card>

        <aside
          aria-label={t.barcode.preview.title}
          className="min-w-0 lg:sticky lg:top-6 lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:self-start"
        >
          <Card className="rounded-lg">
            <CardContent className="p-4 sm:p-5">
              <div className="relative grid min-h-44 place-items-center overflow-auto rounded-lg border bg-white p-5">
                <svg
                  ref={svgRef}
                  className={cn(
                    'max-w-full text-slate-950',
                    (previewState === 'empty' || previewState === 'invalid') && 'hidden',
                  )}
                  data-generated-background={generated?.snapshot.background}
                  data-generated-display-value={generated?.snapshot.displayValue}
                  data-generated-format={generated?.format}
                  data-generated-height={generated?.snapshot.height}
                  data-generated-line-color={generated?.snapshot.lineColor}
                  data-generated-value={generated?.value}
                  data-generated-width={generated?.snapshot.width}
                  role="img"
                />
                {previewState === 'empty' && (
                  <div className="grid min-h-44 w-full place-items-center rounded-md border border-dashed border-slate-300 bg-slate-50 p-8 text-center text-sm text-slate-500">
                    {t.barcode.preview.empty}
                  </div>
                )}
                {previewState === 'stale' && generated && (
                  <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-md bg-white/60 backdrop-blur-[1px]">
                    <span className="rounded-md border bg-card px-2.5 py-1 text-xs text-muted-foreground shadow-sm">
                      {t.barcode.preview.pendingUpdate}
                    </span>
                  </div>
                )}
                {previewState === 'pending' && (
                  <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-md bg-white/60 backdrop-blur-[1px]">
                    <span className="rounded-md border bg-card px-2.5 py-1 text-xs text-muted-foreground shadow-sm">
                      {t.barcode.preview.generating}
                    </span>
                  </div>
                )}
              </div>

              <div data-testid="barcode-status" role="status" aria-atomic="true" aria-live="polite" className="sr-only">
                {statusText}
              </div>

              {error && (
                <div role="alert" aria-atomic="true" className="mt-3 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                  {error}
                </div>
              )}

              <div className="mt-3 grid gap-2">
                {(previewState === 'invalid' || previewState === 'empty') && (
                  <Button className="w-full" onClick={forceRegenerate}>
                    {t.barcode.preview.generate}
                  </Button>
                )}
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    disabled={!canExport || isDownloadingPng}
                    onClick={downloadPng}
                    type="button"
                  >
                    {t.barcode.preview.downloadPng}
                  </Button>
                  <Button
                    disabled={!canExport || isDownloadingPng}
                    onClick={downloadSvg}
                    type="button"
                    variant="outline"
                  >
                    {t.barcode.preview.downloadSvg}
                  </Button>
                </div>
              </div>

              {canExport && normalizedPreview && (
                <div className="mt-3 text-center text-xs text-muted-foreground">
                  {t.barcode.preview.normalized}:{' '}
                  <span className="break-words font-mono text-foreground">{normalizedPreview}</span>
                </div>
              )}

              <p className="mt-3 rounded-lg border border-amber-500/35 bg-amber-500/10 px-3 py-2 text-xs leading-5 text-amber-950 dark:text-amber-100">
                {t.barcode.preview.registrationNotice}
              </p>
            </CardContent>
          </Card>
        </aside>

      <Card className="min-w-0 rounded-lg lg:col-start-1">
        <CardHeader>
          <CardTitle>{t.barcode.appearance.title}</CardTitle>
          <CardDescription>
            {t.barcode.appearance.description}
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-5 md:grid-cols-2">
          <label className="space-y-2">
            <span className="text-sm font-medium">{t.barcode.appearance.lineColor}</span>
            <input
              className="h-11 w-full rounded-md border bg-background p-1"
              onChange={(event) => update('lineColor', event.target.value)}
              type="color"
              value={form.lineColor}
            />
          </label>
          <label className="space-y-2">
            <span className="text-sm font-medium">{t.barcode.appearance.background}</span>
            <input
              className="h-11 w-full rounded-md border bg-background p-1"
              onChange={(event) => update('background', event.target.value)}
              type="color"
              value={form.background}
            />
          </label>
          <label className="space-y-2">
            <span className="flex items-center justify-between gap-3 text-sm font-medium">
              {t.barcode.appearance.width}
              <span className="tabular-nums text-muted-foreground">{form.width}px</span>
            </span>
            <input
              className="w-full accent-primary"
              max={4}
              min={1}
              onChange={(event) => update('width', Number(event.target.value))}
              step={0.5}
              type="range"
              value={form.width}
            />
          </label>
          <label className="space-y-2">
            <span className="flex items-center justify-between gap-3 text-sm font-medium">
              {t.barcode.appearance.height}
              <span className="tabular-nums text-muted-foreground">{form.height}px</span>
            </span>
            <input
              className="w-full accent-primary"
              max={180}
              min={72}
              onChange={(event) => update('height', Number(event.target.value))}
              step={8}
              type="range"
              value={form.height}
            />
          </label>
          <label className="flex items-center gap-3 rounded-md border bg-background px-3 py-2 md:col-span-2">
            <input
              checked={form.displayValue}
              className="size-4 accent-primary"
              onChange={(event) => update('displayValue', event.target.checked)}
              type="checkbox"
            />
            <span className="text-sm font-medium">{t.barcode.appearance.displayValue}</span>
          </label>
        </CardContent>
      </Card>
      </div>

      <section className="mt-8 rounded-lg border bg-card p-5 shadow-sm">
        <h2 className="text-lg font-semibold tracking-normal">{t.barcode.faqTitle}</h2>
        <div className="mt-3 space-y-3">
          {t.barcode.faq.map((item) => (
            <div key={item.question}>
              <h3 className="text-sm font-semibold">{item.question}</h3>
              <p className="mt-1 text-sm leading-6 text-muted-foreground">{item.answer}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  )
}

function buildFormatOptions(t: Translations): FormatOption[] {
  return formatOrder.map((format) => buildFormatOption(format, t))
}

function buildFormatOption(format: BarcodeFormat, t: Translations): FormatOption {
  return {
    value: format,
    label: t.barcode.formatOptions[format].label,
  }
}

function jsFormat(format: BarcodeFormat): string {
  switch (format) {
    case 'ean13':
      return 'EAN13'
    case 'ean8':
      return 'EAN8'
    case 'upc':
      return 'UPC'
    case 'code128':
      return 'CODE128'
    case 'code39':
      return 'CODE39'
  }
}

type BarcodeErrors = Translations['barcode']['errors']

function normalizeBarcodeValue(format: BarcodeFormat, value: string, errors: BarcodeErrors): string {
  const trimmed = value.trim()
  if (!trimmed) {
    throw new BarcodeInputError('empty_value', errors.emptyValue)
  }

  switch (format) {
    case 'ean13':
      return normalizeDigitsWithChecksum(trimmed, 12, 13, ean13Checksum, errors.ean13)
    case 'ean8':
      return normalizeDigitsWithChecksum(trimmed, 7, 8, ean8Checksum, errors.ean8)
    case 'upc':
      return normalizeDigitsWithChecksum(trimmed, 11, 12, upcChecksum, errors.upc)
    case 'code128':
      if (trimmed.length > 80) {
        throw new BarcodeInputError('code128_too_long', errors.code128)
      }
      return trimmed
    case 'code39': {
      const upper = trimmed.toUpperCase()
      if (!/^[0-9A-Z .$/+%-]+$/.test(upper) || upper.length > 80) {
        throw new BarcodeInputError('code39_invalid', errors.code39)
      }
      return upper
    }
  }
}

function normalizeDigitsWithChecksum(
  value: string,
  bodyLength: number,
  fullLength: number,
  checksum: (digits: string) => number,
  errorMessage: string,
): string {
  if (!/^\d+$/.test(value) || (value.length !== bodyLength && value.length !== fullLength)) {
    throw new BarcodeInputError('invalid_digits', errorMessage)
  }

  if (value.length === bodyLength) {
    return `${value}${checksum(value)}`
  }

  const body = value.slice(0, bodyLength)
  const expected = checksum(body)
  if (Number(value[bodyLength]) !== expected) {
    throw new BarcodeInputError('invalid_checksum', errorMessage)
  }

  return value
}

function ean13Checksum(digits: string): number {
  return modulo10Checksum(digits, (index) => (index % 2 === 0 ? 1 : 3))
}

function ean8Checksum(digits: string): number {
  return modulo10Checksum(digits, (index) => (index % 2 === 0 ? 3 : 1))
}

function upcChecksum(digits: string): number {
  return modulo10Checksum(digits, (index) => (index % 2 === 0 ? 3 : 1))
}

function modulo10Checksum(digits: string, weightForIndex: (index: number) => number): number {
  const sum = [...digits].reduce((total, digit, index) => (
    total + Number(digit) * weightForIndex(index)
  ), 0)
  return (10 - (sum % 10)) % 10
}

function serializeSvg(svg: SVGSVGElement): string {
  svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg')
  return new XMLSerializer().serializeToString(svg)
}

async function svgToPng(svg: string): Promise<string> {
  const svgBlob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' })
  const svgUrl = URL.createObjectURL(svgBlob)

  try {
    const image = new Image()
    image.decoding = 'async'
    image.src = svgUrl
    await image.decode()

    const canvas = document.createElement('canvas')
    const scale = 2
    canvas.width = Math.ceil(image.naturalWidth * scale)
    canvas.height = Math.ceil(image.naturalHeight * scale)
    const context = canvas.getContext('2d')
    if (!context) {
      throw new Error('Canvas unavailable')
    }

    context.fillStyle = '#ffffff'
    context.fillRect(0, 0, canvas.width, canvas.height)
    context.drawImage(image, 0, 0, canvas.width, canvas.height)
    return canvas.toDataURL('image/png')
  } finally {
    URL.revokeObjectURL(svgUrl)
  }
}

function downloadUrl(url: string, name: string) {
  const link = document.createElement('a')
  link.download = name
  link.href = url
  link.click()
}

function filename(generated: GeneratedBarcode, extension: 'png' | 'svg'): string {
  return `qrka-${generated.format}-${generated.value}-${generated.generatedAt}.${extension}`
}

class BarcodeInputError extends Error {
  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message)
  }
}

function localizeBarcodeError(error: BarcodeInputError, errors: BarcodeErrors): string {
  switch (error.code) {
    case 'preview_unavailable':
      return errors.previewUnavailable
    case 'generation_failed':
      return errors.generationFailed
    default:
      return error.message || errors.generationFailed
  }
}

function normalizeError(cause: unknown): BarcodeInputError {
  if (cause instanceof BarcodeInputError) {
    return cause
  }

  return new BarcodeInputError('generation_failed', 'generation_failed')
}
